import React, { useEffect, useState } from 'react';
import { Form, Input, Select, Button, Row, Col, message } from 'antd';
import apiService, { Lead } from '../../services/api';
import { useCognitoAuth } from '../../contexts/CognitoAuthContext';
import { getUserIdAsNumber, coerceAppBusinessArea } from '../../utils/cognitoUtils';

const { Option } = Select;

interface NewLeadFormProps {
  defaultBusinessArea?: 'telecommunications' | 'energy_solutions';
  onCreated: (lead: Lead) => void;
  onCancel?: () => void;
}

const NewLeadForm: React.FC<NewLeadFormProps> = ({ defaultBusinessArea, onCreated, onCancel }) => {
  const { user } = useCognitoAuth();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [customerType, setCustomerType] = useState<'residential' | 'business'>('residential');

  useEffect(() => {
    form.setFieldsValue({
      business_area: coerceAppBusinessArea(defaultBusinessArea),
      customer_type: 'residential',
      lead_status: 'Entrada',
    });
  }, [defaultBusinessArea, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const postal_code = values.postal_code_4 && values.postal_code_3
        ? `${values.postal_code_4}-${values.postal_code_3}`
        : undefined;

      const payload: any = {
        ...values,
        postal_code,
        lead_status: 'Entrada',
        section: 'entrada',
        created_by: getUserIdAsNumber(user),
        business_area: [coerceAppBusinessArea(values.business_area)],
        customer_type: values.customer_type || 'residential',
      };
      delete payload.postal_code_4;
      delete payload.postal_code_3;

      const created = await apiService.createLead(payload);
      message.success('Lead criado. Pode começar a trabalhar.');
      onCreated(created);
      form.resetFields();
    } catch (error: any) {
      if (error?.errorFields) return; // validation message already shown
      message.error('Erro ao criar lead. Verifique os dados.');
    } finally {
      setSubmitting(false);
    }
  };

  const businessArea = coerceAppBusinessArea(
    (Form.useWatch('business_area', form) as string | undefined) ?? defaultBusinessArea
  );
  const isTelecom = businessArea === 'telecommunications';

  return (
    <Form form={form} layout="vertical" onFinish={handleSubmit}>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item
            name="name"
            label="Nome ou Denominação social"
            rules={[{ required: true, message: 'Insira o nome' }]}
          >
            <Input placeholder="Nome do cliente" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="business_area"
            label="Área de Negócio"
            rules={[{ required: true, message: 'Selecione uma área' }]}
          >
            <Select>
              <Option value="telecommunications">Telecomunicações</Option>
              <Option value="energy_solutions">Energia</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Insira o email' },
              { type: 'email', message: 'Email inválido' },
            ]}
          >
            <Input placeholder="cliente@dominio.com" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="phone"
            label="Telefone"
            rules={[{ required: true, message: 'Insira o telefone' }]}
          >
            <Input placeholder="+351 ..." />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item
            name="dif"
            label="NIF"
            rules={isTelecom ? [{ required: true, message: 'NIF obrigatório para telecom' }] : []}
          >
            <Input placeholder="123456789" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="customer_type"
            label="Tipo de Cliente"
            rules={[{ required: true, message: 'Selecione o tipo' }]}
          >
            <Select onChange={(v) => setCustomerType(v)}>
              <Option value="residential">Residencial</Option>
              <Option value="business">Empresarial</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
      {isTelecom && (
        <Form.Item name="current_provider" label="Operador Atual">
          <Input placeholder="MEO, NOS, Vodafone..." />
        </Form.Item>
      )}
      <Row gutter={12}>
        <Col span={16}>
          <Form.Item name="address" label="Morada">
            <Input placeholder="Rua, número, andar" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="city" label="Cidade">
            <Input placeholder="Cidade" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={6}>
          <Form.Item
            name="postal_code_4"
            label="Cód. Postal (4)"
            rules={[{ pattern: /^\d{4}$/, message: '4 dígitos' }]}
          >
            <Input maxLength={4} placeholder="1000" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name="postal_code_3"
            label="Cód. Postal (3)"
            rules={[{ pattern: /^\d{3}$/, message: '3 dígitos' }]}
          >
            <Input maxLength={3} placeholder="123" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="lead_status" label="Estado Inicial">
            <Select disabled>
              <Option value="Entrada">Entrada</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        {onCancel && (
          <Button onClick={onCancel} disabled={submitting}>
            Cancelar
          </Button>
        )}
        <Button type="primary" loading={submitting} onClick={handleSubmit}>
          Criar e abrir
        </Button>
      </div>
    </Form>
  );
};

export default NewLeadForm;
